import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Check, Sparkles, Zap, X, Gem, AlertTriangle, Calendar, Loader2, Clock, CreditCard, ShieldAlert } from 'lucide-react';

const Billing = () => {
  const { user, api, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [paymentsEnabled, setPaymentsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(null);
  const [purchaseLoading, setPurchaseLoading] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [pendingUpgradePlan, setPendingUpgradePlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  useEffect(() => {
    fetchPaymentStatus();
    handlePayPalReturn();
  }, []);

  const handlePayPalReturn = async () => {
    const token = searchParams.get('token');
    const type = searchParams.get('type');
    
    if (token && type === 'credits') {
      try {
        const res = await api.post(`/payments/capture-order?order_id=${token}`);
        if (res.data.success) {
          toast.success(`Successfully purchased ${res.data.creditsAdded} credits!`);
          refreshUser();
          window.history.replaceState({}, '', '/billing');
        }
      } catch (err) {
        const msg = err.response?.data?.detail;
        toast.error(typeof msg === 'string' ? msg : 'Failed to complete purchase');
      }
    } else if (searchParams.get('success') === 'true') {
      // Retrieve stored subscription data
      const pending = localStorage.getItem('pendingSubscription');
      localStorage.removeItem('pendingSubscription');

      // Get subscription_id from URL (PayPal appends it) or from localStorage
      const subscriptionId = searchParams.get('subscription_id') || (pending ? JSON.parse(pending).subscriptionId : null);
      const plan = pending ? JSON.parse(pending).plan : null;

      // Attempt immediate activation (non-fatal, webhook is the fallback)
      if (subscriptionId && plan) {
        try {
          await api.post(`/payments/subscriptions/activate?subscription_id=${subscriptionId}&plan=${plan}`);
        } catch (err) {
          // Non-fatal: webhook will handle activation
        }
      }

      // Poll refreshUser up to 5 times until accountStatus === 'active'
      let activated = false;
      for (let i = 0; i < 5; i++) {
        const userData = await refreshUser();
        if (userData?.accountStatus === 'active') {
          activated = true;
          break;
        }
        await new Promise(r => setTimeout(r, 1500));
      }

      toast.success('Payment successful! Your plan has been updated.');
      window.history.replaceState({}, '', '/billing');
    } else if (searchParams.get('canceled') === 'true') {
      localStorage.removeItem('pendingSubscription');
      toast.info('Payment canceled');
      window.history.replaceState({}, '', '/billing');
    }
  };

  const fetchPaymentStatus = async () => {
    try {
      const res = await api.get('/payments/status');
      setPaymentsEnabled(res.data.enabled);
    } catch (err) {
      console.error('Failed to load payment status');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan) => {
    if (!paymentsEnabled) {
      toast.error('Payments are temporarily unavailable. Please try again later.');
      return;
    }

    setUpgradeLoading(plan);
    try {
      const res = await api.post(`/payments/create-subscription?plan=${plan}&billing_period=${billingPeriod}`);

      if (res.data.success && res.data.checkoutUrl) {
        // Store subscription data for activation on return
        localStorage.setItem('pendingSubscription', JSON.stringify({
          subscriptionId: res.data.subscriptionId,
          plan: plan,
          billingPeriod: billingPeriod
        }));
        window.location.href = res.data.checkoutUrl;
      } else {
        toast.error('Unable to start checkout. Please try again.');
      }
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Unable to upgrade. Please try again.';
      toast.error(typeof message === 'string' ? message : 'Unable to upgrade. Please try again.');
    } finally {
      setUpgradeLoading(null);
    }
  };

  // Wrapper: shows confirmation modal for existing subscribers changing plans
  const handleUpgradeClick = (plan) => {
    const isExistingSubscriber = isSubscribed && currentPlan && currentPlan !== 'none';
    if (isExistingSubscriber && plan !== currentPlan) {
      setPendingUpgradePlan(plan);
      setShowUpgradeModal(true);
    } else {
      handleUpgrade(plan);
    }
  };

  const confirmUpgrade = () => {
    setShowUpgradeModal(false);
    if (pendingUpgradePlan) {
      handleUpgrade(pendingUpgradePlan);
      setPendingUpgradePlan(null);
    }
  };

  const handlePurchasePack = async (packId) => {
    if (!paymentsEnabled) {
      toast.error('Payments are temporarily unavailable.');
      return;
    }

    setPurchaseLoading(packId);
    try {
      const res = await api.post('/payments/create-order', { packId });
      
      if (res.data.success && res.data.approvalUrl) {
        window.location.href = res.data.approvalUrl;
      } else {
        toast.error('Unable to start checkout. Please try again.');
      }
    } catch (err) {
      const rawDetail = err.response?.data?.detail;
      let message = 'Payment failed. Please try again.';
      
      if (typeof rawDetail === 'string') {
        message = rawDetail;
      } else if (err.message && typeof err.message === 'string') {
        message = err.message;
      }
      
      toast.error(message);
    } finally {
      setPurchaseLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will keep access until the end of your billing period.')) return;
    
    try {
      const res = await api.post('/payments/subscriptions/cancel');
      const message = res.data?.message || 'Subscription cancelled';
      toast.success(typeof message === 'string' ? message : 'Subscription cancelled');
      refreshUser();
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to cancel subscription';
      toast.error(typeof message === 'string' ? message : 'Failed to cancel subscription');
    }
  };

  // Determine user status
  const isTrialActive = user?.accountStatus === 'trial_active';
  const isTrialExpired = user?.accountStatus === 'trial_expired';
  const isSubscribed = user?.accountStatus === 'active';
  const isCancelled = user?.accountStatus === 'cancelled';
  const isGracePeriod = user?.accountStatus === 'grace_period';
  
  // Calculate credits display
  const trialCredits = user?.trialCredits || 0;
  const monthlyCredits = user?.credits || 0;
  const purchasedCredits = user?.purchasedCredits || 0;
  const totalCredits = isTrialActive ? trialCredits : (monthlyCredits + purchasedCredits);

  // Plan checks
  const currentPlan = user?.subscriptionPlan;
  const isCurrentBeginner = currentPlan === 'beginner';
  const isCurrentPro = currentPlan === 'pro';
  const isCurrentMax = currentPlan === 'max';

  // Calculate trial days remaining using floor
  const getTrialDaysRemaining = () => {
    if (!user?.trialEndTimestamp) return 0;
    const endDate = new Date(user.trialEndTimestamp);
    const now = new Date();
    const diffMs = endDate - now;
    if (diffMs <= 0) return 0;
    const fullDaysRemaining = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hasPartialDay = (diffMs % (1000 * 60 * 60 * 24)) > 0;
    return fullDaysRemaining + (hasPartialDay ? 1 : 0);
  };

  const creditPacks = [
    { id: 'starter', name: 'Starter Pack', description: 'Perfect for trying out', credits: 20, price: 5 },
    { id: 'creator', name: 'Creator Pack', description: 'Most popular choice', credits: 50, price: 10 },
    { id: 'pro_boost', name: 'Pro Boost', description: 'Great value', credits: 120, price: 20 },
    { id: 'power', name: 'Power Pack', description: 'For serious creators', credits: 300, price: 40 },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="billing">
      {/* Trial Banner */}
      {isTrialActive && (
        <Card className="bg-gradient-to-r from-violet-500/20 to-cyan-500/20 border-violet-500/30">
          <CardContent className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-violet-500/20 rounded-full">
                <Clock className="w-6 h-6 text-violet-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Free Trial Active</h3>
                <p className="text-slate-300">
                  {getTrialDaysRemaining()} day{getTrialDaysRemaining() !== 1 ? 's' : ''} remaining • {trialCredits} credits left
                </p>
              </div>
            </div>
            <Button 
              className="bg-violet-500 hover:bg-violet-600 text-white"
              onClick={() => handleUpgrade('beginner')}
              disabled={upgradeLoading !== null}
            >
              Upgrade Now
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Trial Expired Banner */}
      {isTrialExpired && (
        <Card className="bg-red-500/10 border-red-500/30" data-testid="billing-banner-trial-expired">
          <CardContent className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-500/20 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Your free trial has ended</h3>
                <p className="text-slate-300">Upgrade to continue creating content.</p>
              </div>
            </div>
            <Button 
              className="bg-violet-500 hover:bg-violet-600 text-white"
              onClick={() => handleUpgrade('beginner')}
              disabled={upgradeLoading !== null}
              data-testid="billing-upgrade-btn"
            >
              Upgrade Now
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Cancelled Banner */}
      {isCancelled && (
        <Card className="bg-amber-500/10 border-amber-500/30" data-testid="billing-banner-cancelled">
          <CardContent className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-500/20 rounded-full">
                <CreditCard className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Your subscription has ended</h3>
                <p className="text-slate-300">Renew your plan to continue using CreatorFlow.</p>
              </div>
            </div>
            <Button 
              className="bg-violet-500 hover:bg-violet-600 text-white"
              onClick={() => handleUpgrade('beginner')}
              disabled={upgradeLoading !== null}
              data-testid="billing-renew-btn"
            >
              Renew Subscription
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Grace Period Banner */}
      {isGracePeriod && (
        <Card className="bg-orange-500/10 border-orange-500/30" data-testid="billing-banner-grace-period">
          <CardContent className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-500/20 rounded-full">
                <ShieldAlert className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Payment issue detected</h3>
                <p className="text-slate-300">Please update your payment method to avoid losing access.</p>
              </div>
            </div>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="billing-update-payment-btn"
            >
              Update Payment
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Payments Disabled Banner */}
      {!paymentsEnabled && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
            <div>
              <p className="text-amber-200 font-medium">Payments temporarily unavailable</p>
              <p className="text-amber-200/70 text-sm">Check back soon.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Credit Balance Section */}
      <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-lg">Your Credit Balance</h2>
            <p className="text-slate-400 text-sm">Credits are used for AI generations</p>
          </div>
          <div className="flex items-center gap-8">
            <div className="text-center">
              <p className="text-3xl font-bold text-cyan-400" data-testid="total-credits">{totalCredits}</p>
              <p className="text-slate-400 text-sm">{isTrialActive ? 'Trial Credits' : 'Total Credits'}</p>
            </div>
            {!isTrialActive && !isTrialExpired && (
              <>
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">{monthlyCredits}</p>
                  <p className="text-slate-400 text-sm">Monthly</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">{purchasedCredits}</p>
                  <p className="text-slate-400 text-sm">Purchased</p>
                </div>
              </>
            )}
          </div>
        </div>
        
        {user?.nextBillingDate && (
          <div className="mt-4 pt-4 border-t border-slate-700/50 flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span className="text-slate-400">Next billing: {new Date(user.nextBillingDate).toLocaleDateString()}</span>
            {user?.subscriptionStatus === 'cancelled' && (
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 ml-2">Cancels at period end</Badge>
            )}
          </div>
        )}
      </div>

      {/* Choose Your Plan Section */}
      <div>
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">Choose Your Creator Plan</h1>
          <p className="text-slate-400">Scale your content creation with the right plan for your goals.</p>
        </div>

        {/* Billing Period Toggle */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <button
            className={`px-4 py-2 rounded-l-full text-sm font-medium border transition-colors ${billingPeriod === 'monthly' ? 'bg-violet-500 border-violet-500 text-white' : 'bg-transparent border-slate-600 text-slate-400 hover:text-white'}`}
            onClick={() => setBillingPeriod('monthly')}
          >
            Monthly
          </button>
          <button
            className={`px-4 py-2 rounded-r-full text-sm font-medium border transition-colors flex items-center gap-2 ${billingPeriod === 'yearly' ? 'bg-violet-500 border-violet-500 text-white' : 'bg-transparent border-slate-600 text-slate-400 hover:text-white'}`}
            onClick={() => setBillingPeriod('yearly')}
          >
            Yearly
            <span className="bg-cyan-500/20 text-cyan-400 text-xs px-2 py-0.5 rounded-full">Save 20%</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Beginner Plan */}
          <Card className="bg-slate-900/30 border-slate-700/50 relative" data-testid="plan-card-beginner">
            <CardContent className="p-6">
              <h3 className="text-2xl font-bold text-white mb-1">Beginner</h3>
              <p className="text-slate-400 text-sm mb-4">Get started creating</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-white">{billingPeriod === 'yearly' ? '$7' : '$9'}</span>
                <span className="text-slate-400">/month</span>
              </div>
              {billingPeriod === 'yearly' && (
                <p className="text-cyan-400 text-xs mb-5">Billed as $84/year</p>
              )}
              {billingPeriod === 'monthly' && <div className="mb-5" />}

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-cyan-400 font-medium">50 credits per month</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-white">Idea Generator</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-white">Script Builder</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-white">Writing Tools (2 credits)</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="w-4 h-4 text-slate-600 shrink-0" />
                  <span className="text-sm text-slate-500">Editor</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="w-4 h-4 text-slate-600 shrink-0" />
                  <span className="text-sm text-slate-500">AI Voiceovers</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="w-4 h-4 text-slate-600 shrink-0" />
                  <span className="text-sm text-slate-500">30-Day Planner</span>
                </div>
              </div>

              <Button
                className={`w-full ${isCurrentBeginner 
                  ? 'bg-slate-700 text-slate-300 cursor-not-allowed' 
                  : 'bg-transparent border border-slate-600 text-white hover:bg-slate-800'}`}
                onClick={() => handleUpgradeClick('beginner')}
                disabled={isCurrentBeginner || upgradeLoading === 'beginner' || !paymentsEnabled}
                data-testid="select-plan-beginner"
              >
                {upgradeLoading === 'beginner' ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : isCurrentBeginner ? 'Current Plan' : (isTrialActive || isTrialExpired) ? 'Subscribe' : 'Switch'}
              </Button>
            </CardContent>
          </Card>

          {/* Pro Plan */}
          <Card className="bg-slate-800/50 border-violet-500/50 relative" data-testid="plan-card-pro">
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-violet-500 text-white border-0 px-3 py-1">
              MOST POPULAR
            </Badge>
            <CardContent className="p-6 pt-8">
              <h3 className="text-2xl font-bold text-violet-400 mb-1">Pro Creator</h3>
              <p className="text-slate-400 text-sm mb-4">For serious creators</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-white">{billingPeriod === 'yearly' ? '$15' : '$19'}</span>
                <span className="text-slate-400">/month</span>
              </div>
              {billingPeriod === 'yearly' && (
                <p className="text-cyan-400 text-xs mb-5">Billed as $180/year</p>
              )}
              {billingPeriod === 'monthly' && <div className="mb-5" />}

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-cyan-400 font-medium">200 credits per month</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-white">Idea Generator</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-white">Script Builder</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-white">Writing Tools (2 credits)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-white">Editor</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-white">AI Voiceovers</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-white">Content Analyzer (15 credits)</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="w-4 h-4 text-slate-600 shrink-0" />
                  <span className="text-sm text-slate-500">30-Day Planner</span>
                </div>
              </div>

              <Button
                className={`w-full ${isCurrentPro 
                  ? 'bg-slate-700 text-slate-300 cursor-not-allowed' 
                  : 'bg-violet-500 hover:bg-violet-600 text-white'}`}
                onClick={() => handleUpgradeClick('pro')}
                disabled={isCurrentPro || upgradeLoading === 'pro' || !paymentsEnabled}
                data-testid="select-plan-pro"
              >
                {upgradeLoading === 'pro' ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : isCurrentPro ? 'Current Plan' : (isTrialActive || isTrialExpired) ? 'Subscribe' : 'Upgrade'}
              </Button>
            </CardContent>
          </Card>

          {/* Max Plan */}
          <Card className="bg-slate-900/30 border-cyan-500/50 relative" data-testid="plan-card-max">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                <h3 className="text-2xl font-bold text-cyan-400">Creator Max</h3>
              </div>
              <p className="text-slate-400 text-sm mb-4">For power creators</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-white">{billingPeriod === 'yearly' ? '$39' : '$49'}</span>
                <span className="text-slate-400">/month</span>
              </div>
              {billingPeriod === 'yearly' && (
                <p className="text-cyan-400 text-xs mb-5">Billed as $468/year</p>
              )}
              {billingPeriod === 'monthly' && <div className="mb-5" />}

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-cyan-400 font-medium">600 credits per month</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-white">All Pro Creator features</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-white">Priority AI processing</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-cyan-400 font-medium">30-Day Planner (Exclusive)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="text-sm text-cyan-400 font-medium">Content Analyzer (20 credits)</span>
                </div>
              </div>

              <Button
                className={`w-full ${isCurrentMax 
                  ? 'bg-cyan-700 text-white cursor-not-allowed' 
                  : 'bg-transparent border border-cyan-500 text-cyan-400 hover:bg-cyan-500/10'}`}
                onClick={() => handleUpgradeClick('max')}
                disabled={isCurrentMax || upgradeLoading === 'max' || !paymentsEnabled}
                data-testid="select-plan-max"
              >
                {upgradeLoading === 'max' ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : isCurrentMax ? 'Current Plan' : (isTrialActive || isTrialExpired) ? 'Subscribe' : 'Upgrade'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Cancel subscription button */}
        {user?.paypalSubscriptionId && user?.subscriptionStatus !== 'cancelled' && (
          <div className="text-center mt-4">
            <Button
              variant="ghost"
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              onClick={handleCancelSubscription}
            >
              Cancel Subscription
            </Button>
          </div>
        )}
      </div>

      {/* Buy Credit Packs Section - Available to all users */}
      <div>
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Gem className="w-6 h-6 text-violet-400" />
            <h2 className="text-3xl font-bold bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
              Buy Credit Packs
            </h2>
          </div>
          <p className="text-slate-400">
            Need more credits? Purchase one-time credit packs that never expire and<br />
            stack with your existing credits.
          </p>
        </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {creditPacks.map((pack) => (
              <Card 
                key={pack.id} 
                className="bg-slate-900/30 border-slate-700/50 hover:border-slate-600/50 transition-colors"
                data-testid={`credit-pack-${pack.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4 text-violet-400" />
                    <h3 className="text-lg font-bold text-white">{pack.name}</h3>
                  </div>
                  <p className="text-slate-400 text-sm mb-6">{pack.description}</p>
                  
                  <div className="text-center mb-6">
                    <p className="text-4xl font-bold text-violet-400">{pack.credits}</p>
                    <p className="text-slate-400 text-sm">credits</p>
                  </div>

                  <p className="text-center text-white font-bold mb-4">
                    ${pack.price} <span className="text-slate-400 font-normal text-sm">one-time</span>
                  </p>

                  <Button
                    className="w-full bg-transparent border border-slate-600 text-white hover:bg-slate-800"
                    onClick={() => handlePurchasePack(pack.id)}
                    disabled={purchaseLoading === pack.id || !paymentsEnabled}
                    data-testid={`purchase-pack-${pack.id}`}
                  >
                    {purchaseLoading === pack.id ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                    ) : 'Purchase'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-center text-slate-500 text-sm mt-6">
            Purchased credits never expire and are used after your monthly credits run out.
          </p>
          <div className="flex justify-center gap-4 mt-4 text-xs text-slate-500">
            <Link to="/terms" className="hover:text-violet-400">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-violet-400">Privacy Policy</Link>
            <Link to="/help" className="hover:text-violet-400">Help & Support</Link>
          </div>
        </div>

      {/* Upgrade Confirmation Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="upgrade-confirm-modal">
          <Card className="bg-slate-900 border-slate-700 max-w-md w-full mx-4 shadow-2xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-amber-500/15 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="text-lg font-bold text-white">Upgrading will replace your current monthly credits</h3>
              </div>
              <p className="text-slate-400 text-sm mb-6">
                Your remaining subscription credits will be replaced with the new plan's credit amount. Purchased credit packs will not be affected.
              </p>
              <div className="flex gap-3">
                <Button
                  className="flex-1 bg-violet-500 hover:bg-violet-600 text-white"
                  onClick={confirmUpgrade}
                  data-testid="confirm-upgrade-btn"
                >
                  Confirm Upgrade
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                  onClick={() => { setShowUpgradeModal(false); setPendingUpgradePlan(null); }}
                  data-testid="cancel-upgrade-btn"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Billing;
